import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../config/firebase';
import { useOffersStore } from '../stores/offersStore';
import { useConventionsStore } from '../stores/conventionsStore';
import JobOfferForm from '../components/JobOfferForm';
import JobOfferDetails from '../components/JobOfferDetails';
import JobFilters from '../components/JobFilters';
import JobOffersImport from '../components/offers/JobOffersImport';
import JobStatusBadge from '../components/JobStatusBadge';
import UrgencyBadge from '../components/UrgencyBadge';
import { Building2, MapPin, Calendar, Upload, Plus, Search, Filter, Trash2, Download, Users, LayoutGrid, List, User, ChevronLeft, ChevronRight, Send, Bot, Sparkles, X, Loader2, CheckCircle2, ExternalLink, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { useThemeStore } from '../stores/themeStore';
import { checkAdminStatus } from '../utils/checkAdminStatus';

export default function Offres() {
  const navigate = useNavigate();
  const { offers, aiOffers, isAiScraping, loadOffers, addOffer, updateOffer, deleteOffer, deleteAllOffers, scrapeAiOffers, loadAiOffers, importAiOffer, importAllAiOffers } = useOffersStore();
  const { loadAllEmployees, loadConventions, conventions, employees: conventionEmployees } = useConventionsStore();
  const [isAdmin, setIsAdmin] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showNewForm, setShowNewForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [isRefreshingPostes, setIsRefreshingPostes] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState<any>(null);
  const [editingOffer, setEditingOffer] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOffers, setSelectedOffers] = useState<Set<string>>(new Set());
  const [aiCommunes, setAiCommunes] = useState('');
  const [aiSearchMode, setAiSearchMode] = useState<'poste' | 'projet' | 'libre'>('poste');
  const [selectedPostes, setSelectedPostes] = useState<string[]>([]);
  const [manualPostes, setManualPostes] = useState<string[]>([]);
  const [manualPosteInput, setManualPosteInput] = useState('');
  const [selectedConventionId, setSelectedConventionId] = useState<string>('all');
  const { darkMode } = useThemeStore();

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async user => {
      if (!user) {
        navigate('/login');
        return;
      }
      loadOffers();
      const admin = await checkAdminStatus(user.email || '');
      setIsAdmin(admin);
    });

    return () => unsubscribe();
  }, [navigate, loadOffers]);

  useEffect(() => {
    loadAllEmployees();
    loadConventions();
    loadAiOffers();
  }, [loadAllEmployees, loadConventions, loadAiOffers]);

  const filteredEmployees = useMemo(() => {
    if (selectedConventionId === 'all') return conventionEmployees;
    return conventionEmployees.filter(e => e.convention_id === selectedConventionId);
  }, [conventionEmployees, selectedConventionId]);

  const candidatePostes = useMemo(() => {
    const postes = new Set<string>();
    filteredEmployees.forEach(e => {
      if (aiSearchMode === 'projet') {
        const projet = e.projet_valide?.trim();
        if (projet) postes.add(projet);
      } else if (e.poste_actuel) postes.add(e.poste_actuel);
      else if (e.poste) postes.add(e.poste);
    });
    return Array.from(postes).filter(Boolean).sort();
  }, [filteredEmployees, aiSearchMode]);

  useEffect(() => {
    setSelectedPostes([]);
  }, [candidatePostes]);

  const handleTogglePoste = (poste: string) => {
    setSelectedPostes(current => current.includes(poste)
      ? current.filter(selected => selected !== poste)
      : [...current, poste]
    );
  };

  const handleAddManualPoste = () => {
    const value = manualPosteInput.trim();
    if (!value) return;
    if (!manualPostes.includes(value)) {
      setManualPostes(prev => [...prev, value]);
    }
    setManualPosteInput('');
  };

  const handleRemoveManualPoste = (poste: string) => {
    setManualPostes(prev => prev.filter(p => p !== poste));
  };

  const handleAiScrape = async () => {
    const allPostes = aiSearchMode === 'libre' ? manualPostes : selectedPostes;
    if (allPostes.length === 0) {
      toast.error('Sélectionnez ou saisissez au moins un poste à rechercher');
      return;
    }
    const deptInput = aiCommunes.trim()
      ? aiCommunes.split(',').map(c => c.trim()).filter(Boolean)
      : [];
    try {
      const result = await scrapeAiOffers(allPostes, deptInput);
      result.warnings.forEach(w => toast.error(w, { duration: 6000 }));
      if (!result.adzunaConfigured) {
        toast('Adzuna n’est pas encore activé : seules les offres France Travail ont été recherchées.', { duration: 6000 });
      }
      const bySource = Object.entries(result.foundBySource)
        .filter(([, count]) => count > 0)
        .map(([source, count]) => `${source} : ${count}`)
        .join(' · ');
      if (bySource) toast(bySource, { duration: 5000 });
      if (result.found === 0) {
        toast.error('Aucune offre trouvée pour les postes sélectionnés');
      } else if (result.inserted === 0) {
        toast.success(`${result.found} offres trouvées, toutes déjà importées`);
      } else {
        const msg = result.alreadyExisting > 0
          ? `${result.inserted} nouvelle${result.inserted > 1 ? 's' : ''} offre${result.inserted > 1 ? 's' : ''} importée${result.inserted > 1 ? 's' : ''} (${result.alreadyExisting} déjà existante${result.alreadyExisting > 1 ? 's' : ''})`
          : `${result.inserted} offre${result.inserted > 1 ? 's' : ''} importée${result.inserted > 1 ? 's' : ''}`;
        toast.success(msg);
      }
    } catch (error: any) {
      toast.error(error.message || 'Erreur lors de la recherche IA');
    }
  };

  const normalizeSearchText = (value: string): string =>
    value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

  const getInitials = (name: string): string => {
    if (!name || name === 'IA') return 'IA';
    return name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map(part => part[0])
      .join('')
      .toUpperCase();
  };

  const filteredOffers = offers.filter(offer => {
    const query = normalizeSearchText(searchTerm);
    if (!query) return true;

    const searchableValues = [
      offer.title,
      offer.company,
      offer.location,
      offer.createdBy || '',
      getInitials(offer.createdBy || ''),
      offer.isAiGenerated ? 'IA' : ''
    ].map(normalizeSearchText);

    return searchableValues.some(value => value.includes(query));
  });

  const totalPages = Math.ceil(filteredOffers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedOffers = filteredOffers.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleEdit = (offer: any) => {
    setEditingOffer(offer);
    setShowNewForm(true);
  };

  const handleSubmit = async (data: any) => {
    try {
      if (editingOffer) {
        await updateOffer(editingOffer.id, data);
        toast.success('Offre mise à jour avec succès');
      } else {
        await addOffer({
          ...data,
          status: 'new',
          candidates: [],
          createdAt: new Date().toISOString()
        });
        toast.success('Offre créée avec succès');
      }
      setShowNewForm(false);
      setEditingOffer(null);
    } catch (error) {
      console.error('Error submitting offer:', error);
      toast.error('Erreur lors de l\'enregistrement de l\'offre');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette offre ?')) {
      try {
        await deleteOffer(id);
        setSelectedOffer(null);
        toast.success('Offre supprimée avec succès');
      } catch (error) {
        console.error('Error deleting offer:', error);
        toast.error('Erreur lors de la suppression de l\'offre');
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedOffers.size === 0) return;

    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedOffers.size} offre${selectedOffers.size > 1 ? 's' : ''} ?`)) {
      try {
        const promises = Array.from(selectedOffers).map(id => deleteOffer(id));
        await Promise.all(promises);
        setSelectedOffers(new Set());
        toast.success(`${selectedOffers.size} offre${selectedOffers.size > 1 ? 's' : ''} supprimée${selectedOffers.size > 1 ? 's' : ''} avec succès`);
      } catch (error) {
        console.error('Error deleting offers:', error);
        toast.error('Erreur lors de la suppression des offres');
      }
    }
  };

  const handleImport = async (data: any[]) => {
    try {
      for (const offer of data) {
        await addOffer(offer);
      }
      setShowImport(false);
      toast.success(`${data.length} offre${data.length > 1 ? 's' : ''} importée${data.length > 1 ? 's' : ''} avec succès`);
    } catch (error) {
      console.error('Error importing offers:', error);
      toast.error('Erreur lors de l\'import des offres');
    }
  };

  const buildOfferRows = (list: typeof offers) => list.map(o => ({
    'ID Utilisateur': auth.currentUser?.uid || '',
    'Utilisateur': auth.currentUser?.displayName || 'Non renseigné',
    'Titre': o.title,
    'Entreprise': o.company,
    'Localisation': o.location,
    'Type de contrat': o.type,
    'Niveau d\'urgence': o.urgencyLevel === 'high' ? 'Urgent' :
                           o.urgencyLevel === 'medium' ? 'Prioritaire' :
                           'Normal',
    'Statut': o.status === 'new' ? 'Nouvelle' :
                 o.status === 'open' ? 'En cours' :
                 o.status === 'filled' ? 'Pourvue' :
                 'Fermée',
    'Description': o.description,
    'Salaire': o.salary || '',
    'Candidatures': o.candidates?.length || 0,
    'Créée par': o.createdBy || 'Non spécifié',
    'Contact - Nom': o.contact?.name || '',
    'Contact - Fonction': o.contact?.role || '',
    'Contact - Email': o.contact?.email || '',
    'Contact - Téléphone': o.contact?.phone || '',
    'Date de création': new Date(o.createdAt).toLocaleDateString('fr-FR')
  }));

  const handleExport = () => {
    if (!auth.currentUser) {
      toast.error('Vous devez être connecté pour exporter les données');
      return;
    }

    if (selectedOffers.size === 0) {
      toast.error('Sélectionnez au moins une offre à exporter');
      return;
    }

    try {
      const selectedOffersList = offers.filter(o => selectedOffers.has(o.id));
      const data = buildOfferRows(selectedOffersList);

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);

      // Ajuster la largeur des colonnes
      const colWidths = Object.keys(data[0]).map(key => ({
        wch: Math.max(20, key.length)
      }));
      ws['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, 'Offres');
      XLSX.writeFile(wb, 'export_offres.xlsx');

      toast.success(`${selectedOffers.size} offre${selectedOffers.size > 1 ? 's' : ''} exportée${selectedOffers.size > 1 ? 's' : ''} avec succès`);
    } catch (error) {
      console.error('Error exporting offers:', error);
      toast.error('Erreur lors de l\'export des offres');
    }
  };

  const handleExportAll = () => {
    if (!auth.currentUser) {
      toast.error('Vous devez être connecté pour exporter les données');
      return;
    }

    if (offers.length === 0) {
      toast.error('Aucune offre à exporter');
      return;
    }

    try {
      const data = buildOfferRows(offers);

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(data);

      const colWidths = Object.keys(data[0]).map(key => ({
        wch: Math.max(20, key.length)
      }));
      ws['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, 'Offres');
      const dateStr = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `export_toutes_offres_${dateStr}.xlsx`);

      toast.success(`${offers.length} offre${offers.length > 1 ? 's' : ''} exportée${offers.length > 1 ? 's' : ''} avec succès`);
    } catch (error) {
      console.error('Error exporting all offers:', error);
      toast.error('Erreur lors de l\'export des offres');
    }
  };

  const toggleOffer = (id: string) => {
    setSelectedOffers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getFullName = (initials: string, createdBy: string): string => {
    return createdBy || 'Utilisateur inconnu';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Offres d'emploi</h1>
        <div className="flex gap-4">
          <button
            onClick={handleExport}
            disabled={selectedOffers.size === 0}
            className={`hidden md:flex items-center px-4 py-2 text-sm ${darkMode ? 'bg-green-700 text-white' : 'bg-green-600 text-white'} rounded-2xl hover:${darkMode ? 'bg-green-600' : 'bg-green-700'} disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Download className="w-4 h-4 mr-2" />
            Exporter ({selectedOffers.size})
          </button>
          <button
            onClick={handleExportAll}
            disabled={offers.length === 0}
            className={`hidden md:flex items-center px-4 py-2 text-sm ${darkMode ? 'bg-teal-700 text-white' : 'bg-teal-600 text-white'} rounded-2xl hover:${darkMode ? 'bg-teal-600' : 'bg-teal-700'} disabled:opacity-50 disabled:cursor-not-allowed`}
            title="Exporter toutes les offres sans sélection"
          >
            <Download className="w-4 h-4 mr-2" />
            Exporter tout ({offers.length})
          </button>
          <button
            onClick={() => setShowImport(true)}
            className={`hidden md:flex items-center px-4 py-2 border ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-200 text-gray-600'} rounded-2xl hover:${darkMode ? 'bg-gray-700' : 'bg-gray-50'} transition-colors`}
          >
            <Upload className="w-5 h-5 mr-2" />
            Import
          </button>
          <button
            onClick={() => setShowAiPanel(!showAiPanel)}
            className={`flex items-center px-4 py-2 rounded-2xl transition-all ${showAiPanel ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white' : `${darkMode ? 'border border-purple-600 text-purple-300' : 'border border-purple-200 text-purple-600'} hover:bg-purple-50 dark:hover:bg-purple-900/20`}`}
            title="Rechercher des offres avec l'IA"
          >
            {isAiScraping ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Bot className="w-5 h-5 mr-2" />}
            Recherche IA
          </button>
          <button
            onClick={() => {
              setEditingOffer(null);
              setShowNewForm(true);
            }}
            className="flex items-center px-4 py-2 bg-gradient-anthea text-white rounded-2xl hover:opacity-90 transition-opacity"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nouvelle offre
          </button>
          {isAdmin && (
            <button
              onClick={async () => {
                if (offers.length === 0) {
                  toast.error('Aucune offre à supprimer');
                  return;
                }
                if (window.confirm(`Êtes-vous sûr de vouloir supprimer toutes les ${offers.length} offres ? Cette action est irréversible.`)) {
                  try {
                    await deleteAllOffers();
                    setSelectedOffers(new Set());
                  } catch (error) {
                    console.error('Error deleting all offers:', error);
                  }
                }
              }}
              disabled={offers.length === 0}
              className={`flex items-center px-4 py-2 text-sm ${darkMode ? 'bg-red-900 text-red-300' : 'bg-red-50 text-red-600'} rounded-2xl hover:${darkMode ? 'bg-red-800' : 'bg-red-100'} disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer tout
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Rechercher une offre, une entreprise ou un consultant (SL, HB, AG)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full pl-10 pr-4 py-2 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-anthea-blue ${darkMode ? 'bg-gray-700 text-white border-gray-600' : ''}`}
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFilters(true)}
            className={`flex items-center px-4 py-2 border ${darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-200 text-gray-600'} rounded-2xl hover:${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}
          >
            <Filter className="w-5 h-5 mr-2" />
            Filtres
          </button>
          <div className={`flex gap-2 border ${darkMode ? 'border-gray-600' : 'border-gray-200'} rounded-2xl p-1`}>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-xl ${viewMode === 'grid' ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : (darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50')}`}
            >
              <LayoutGrid className={`w-5 h-5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-xl ${viewMode === 'list' ? (darkMode ? 'bg-gray-700' : 'bg-gray-100') : (darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50')}`}
            >
              <List className={`w-5 h-5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
            </button>
          </div>
        </div>
      </div>

      {selectedOffers.size > 0 && (
        <div className={`flex justify-between items-center p-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} rounded-xl`}>
          <div className="flex items-center gap-4">
            <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {selectedOffers.size} offre{selectedOffers.size > 1 ? 's' : ''} sélectionnée{selectedOffers.size > 1 ? 's' : ''}
            </span>
          </div>
          <button
            onClick={handleDeleteSelected}
            className={`flex items-center px-3 py-1.5 text-sm ${darkMode ? 'bg-red-900 text-red-300' : 'bg-red-50 text-red-600'} rounded-lg hover:${darkMode ? 'bg-red-800' : 'bg-red-100'}`}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Supprimer la sélection
          </button>
        </div>
      )}

      {showAiPanel && (
        <div className={`mb-6 rounded-2xl border-2 ${darkMode ? 'bg-gray-800 border-purple-700' : 'bg-white border-purple-200'} overflow-hidden`}>
          <div className={`px-5 py-4 ${darkMode ? 'bg-purple-900/30' : 'bg-purple-50'} border-b ${darkMode ? 'border-purple-800' : 'border-purple-200'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Recherche d'offres IA</h3>
                  <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Récupère automatiquement les offres correspondant aux postes ou projets validés des candidats en reclassement
                  </p>
                </div>
              </div>
              <button onClick={() => setShowAiPanel(false)} className={`${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'}`}>
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="p-5 space-y-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Filtrer par convention
              </label>
              <select
                value={selectedConventionId}
                onChange={e => setSelectedConventionId(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg text-sm border transition-colors ${darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'} focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
              >
                <option value="all">Toutes les conventions</option>
                {conventions.map(c => (
                  <option key={c.id} value={c.id}>{c.nom} - {c.entreprise_nom}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Rechercher par
              </label>
              <div className={`inline-flex p-1 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                {([
                  { key: 'poste', label: 'Poste des candidats' },
                  { key: 'projet', label: 'Projet validé' },
                  { key: 'libre', label: 'Recherche libre' },
                ] as const).map(mode => (
                  <button
                    key={mode.key}
                    type="button"
                    onClick={() => setAiSearchMode(mode.key)}
                    aria-pressed={aiSearchMode === mode.key}
                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${aiSearchMode === mode.key
                      ? darkMode ? 'bg-gray-900 text-purple-200 shadow-sm' : 'bg-white text-purple-700 shadow-sm'
                      : darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-800'
                    }`}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            </div>
            {aiSearchMode !== 'libre' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {aiSearchMode === 'projet' ? 'Projets validés des candidats' : 'Postes de candidats en reclassement'} ({selectedPostes.length} sélectionné{selectedPostes.length > 1 ? 's' : ''} sur {candidatePostes.length})
                </label>
                <button
                  type="button"
                  disabled={isRefreshingPostes}
                  onClick={async () => {
                    setIsRefreshingPostes(true);
                    try {
                      await Promise.all([loadAllEmployees(), loadConventions()]);
                      toast.success('Liste des postes actualisée');
                    } catch {
                      toast.error('Erreur lors de l\'actualisation');
                    } finally {
                      setIsRefreshingPostes(false);
                    }
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                  title="Actualiser la liste des postes"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isRefreshingPostes ? 'animate-spin' : ''}`} />
                  Actualiser
                </button>
              </div>
              {candidatePostes.length > 0 && (
                <div className="flex items-center gap-3 mb-3">
                  <button
                    type="button"
                    onClick={() => setSelectedPostes(candidatePostes)}
                    className={`text-xs font-medium ${darkMode ? 'text-purple-300 hover:text-purple-200' : 'text-purple-700 hover:text-purple-800'}`}
                  >
                    Tout sélectionner
                  </button>
                  <span className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-300'}`}>|</span>
                  <button
                    type="button"
                    onClick={() => setSelectedPostes([])}
                    className={`text-xs font-medium ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'}`}
                  >
                    Tout désélectionner
                  </button>
                </div>
              )}
              <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                {candidatePostes.length === 0 ? (
                  <p className={`text-sm italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{aiSearchMode === 'projet' ? 'Aucun projet validé renseigné dans le reclassement' : 'Aucun poste trouvé dans le reclassement'}</p>
                ) : (
                  candidatePostes.map(poste => {
                    const isSelected = selectedPostes.includes(poste);
                    return (
                      <button
                        key={poste}
                        type="button"
                        onClick={() => handleTogglePoste(poste)}
                        aria-pressed={isSelected}
                        className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${isSelected
                          ? darkMode
                            ? 'bg-purple-900/70 text-purple-200 border-purple-600'
                            : 'bg-purple-100 text-purple-800 border-purple-300'
                          : darkMode
                            ? 'bg-gray-700 text-gray-400 border-gray-600 hover:border-purple-700 hover:text-purple-300'
                            : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-purple-300 hover:text-purple-700'
                        }`}
                      >
                        {poste}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
            )}
            {aiSearchMode === 'libre' && (
            <div>
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Recherche libre (saisir un poste manuellement)
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={manualPosteInput}
                  onChange={e => setManualPosteInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddManualPoste(); } }}
                  placeholder="ex: Développeur web, Chef de projet..."
                  className={`flex-1 px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-purple-500`}
                />
                <button
                  type="button"
                  onClick={handleAddManualPoste}
                  disabled={!manualPosteInput.trim()}
                  className="px-4 py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Ajouter
                </button>
              </div>
              {manualPostes.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {manualPostes.map(poste => (
                    <span
                      key={poste}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-purple-900/70 text-purple-200 border border-purple-600' : 'bg-purple-100 text-purple-800 border border-purple-300'}`}
                    >
                      {poste}
                      <button
                        type="button"
                        onClick={() => handleRemoveManualPoste(poste)}
                        className="ml-0.5 hover:text-red-500 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            )}
            <div>
              <label className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Département (optionnel)
              </label>
              <input
                type="text"
                value={aiCommunes}
                onChange={e => setAiCommunes(e.target.value)}
                placeholder="ex: 75, 69, 42"
                className={`w-full px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-purple-500`}
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleAiScrape}
                disabled={isAiScraping || (aiSearchMode === 'libre' ? manualPostes.length === 0 : selectedPostes.length === 0)}
                className="flex items-center px-5 py-2.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAiScraping ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Recherche en cours...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Lancer la recherche IA
                  </>
                )}
              </button>
            </div>
            {aiOffers.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Offres IA trouvées ({aiOffers.filter(o => !o.imported_to_offers).length} nouvelles)
                  </h4>
                  {aiOffers.filter(o => !o.imported_to_offers).length > 0 && (
                    <button
                      onClick={async () => {
                        try { await importAllAiOffers(); } catch {}
                      }}
                      className="px-3 py-1.5 text-xs font-medium bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:opacity-90 transition-opacity"
                    >
                      Tout importer ({aiOffers.filter(o => !o.imported_to_offers).length})
                    </button>
                  )}
                </div>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {aiOffers.map(aiOffer => (
                    <div
                      key={aiOffer.id}
                      className={`flex items-center justify-between p-3 rounded-xl border ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} ${aiOffer.imported_to_offers ? 'opacity-50' : ''}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'} truncate`}>{aiOffer.title}</span>
                          {aiOffer.imported_to_offers && (
                            <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                          )}
                        </div>
                        <p className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"} truncate`}>{aiOffer.company} - {aiOffer.location || "Lieu non précisé"}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            Correspond à: {aiOffer.matched_keyword}
                          </span>
                        </div>
                      </div>
                      {!aiOffer.imported_to_offers && (
                        <button
                          onClick={() => importAiOffer(aiOffer.id)}
                          className="flex-shrink-0 ml-3 px-3 py-1.5 text-xs font-medium bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:opacity-90 transition-opacity"
                        >
                          Importer
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {paginatedOffers.map((offer) => {
            const isAi = offer.isAiGenerated || offer.createdBy === 'IA';
            const initials = isAi ? 'IA' : getInitials(offer.createdBy || '');
            const fullName = isAi ? `IA - ${offer.aiSource || 'France Travail'}` : getFullName(initials, offer.createdBy || '');
            
            return (
              <div
                key={offer.id}
                className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm hover:shadow-md transition-shadow cursor-pointer relative ${isAi ? 'ring-1 ring-purple-300' : ''}`}
                onClick={() => setSelectedOffer(offer)}
              >
                <div className="absolute top-4 left-4 z-10">
                  <div onClick={e => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedOffers.has(offer.id)}
                      onChange={() => toggleOffer(offer.id)}
                      className={`h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 ${darkMode ? 'bg-gray-600 border-gray-500' : ''}`}
                    />
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className={`font-semibold text-lg ${darkMode ? 'text-white' : 'text-gray-900'}`}>{offer.title}</h3>
                      <div className="flex items-center gap-2">
                        <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{offer.company}</p>
                        <div 
                          className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold group relative flex-shrink-0 ${isAi ? 'bg-gradient-to-br from-red-500 to-red-600 text-white' : darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}
                          title={fullName}
                        >
                          {initials}
                          <span className="hidden group-hover:block absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs py-1 px-2 rounded whitespace-nowrap">
                            {fullName}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <UrgencyBadge level={offer.urgencyLevel} />
                      <JobStatusBadge status={offer.status} />
                      <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-blue-900 text-blue-300 border border-blue-800' : 'bg-blue-50 text-blue-700 border border-blue-100'}`}>
                        <Users className="w-3.5 h-3.5" />
                        {offer.candidates?.length || 0} candidature{(offer.candidates?.length || 0) > 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>

                  <div className={`space-y-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <div className="flex items-center">
                      <Building2 className="w-4 h-4 mr-2" />
                      {offer.type}
                      {offer.duration && (
                        <span className="ml-2 text-xs">
                          ({offer.duration === 'indeterminee' ? 'Indéterminée' : `${offer.duration} mois`})
                        </span>
                      )}
                    </div>
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-2" />
                      {offer.location}
                    </div>
                    {offer.transmissionStatus && (
                      <div className="flex items-center">
                        <Send className="w-4 h-4 mr-2" />
                        <span className="text-xs">
                          {offer.transmissionStatus === 'jobboard' ? 'Offres jobboard' :
                           offer.transmissionStatus === 'prospection' ? 'Offres prospection' :
                           offer.transmissionStatus === 'ove' ? 'OVE' :
                           offer.transmissionStatus === 'ofe' ? 'OFE' :
                           'CV direct entreprises'}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      {new Date(offer.createdAt).toLocaleDateString('fr-FR')}
                    </div>
                    {isAi && offer.sourceUrl && (
                      <div className="flex items-center">
                        <ExternalLink className="w-4 h-4 mr-2" />
                        <a
                          href={offer.sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          className="text-xs text-blue-600 hover:text-blue-800 hover:underline truncate max-w-[200px]"
                        >
                          Voir l'offre originale
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-sm divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
          {paginatedOffers.map((offer) => {
            const isAi = offer.isAiGenerated || offer.createdBy === 'IA';
            const initials = isAi ? 'IA' : getInitials(offer.createdBy || '');
            const fullName = isAi ? `IA - ${offer.aiSource || 'France Travail'}` : getFullName(initials, offer.createdBy || '');
            
            return (
              <div
                key={offer.id}
                className={`flex items-center p-4 hover:${darkMode ? 'bg-gray-700' : 'bg-gray-50'} transition-colors cursor-pointer ${isAi ? 'bg-purple-50 dark:bg-purple-900/20' : ''}`}
                onClick={() => setSelectedOffer(offer)}
              >
                <div className="mr-4" onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selectedOffers.has(offer.id)}
                    onChange={() => toggleOffer(offer.id)}
                    className={`h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 ${darkMode ? 'bg-gray-600 border-gray-500' : ''}`}
                  />
                </div>
                <div className="flex-1 grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-4">
                    <div className="flex items-center gap-2">
                      <h3 className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{offer.title}</h3>
                      <div 
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold group relative flex-shrink-0 ${isAi ? 'bg-gradient-to-br from-red-500 to-red-600 text-white' : darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}
                        title={fullName}
                      >
                        {initials}
                        <span className="hidden group-hover:block absolute bottom-full mb-1 left-1/2 transform -translate-x-1/2 bg-black text-white text-xs py-1 px-2 rounded whitespace-nowrap">
                          {fullName}
                        </span>
                      </div>
                    </div>
                    <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{offer.company}</p>
                  </div>
                  <div className={`col-span-2 flex items-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <Building2 className="w-4 h-4 mr-2" />
                    {offer.type}
                    {offer.duration && (
                      <span className="ml-2 text-xs">
                        ({offer.duration === 'indeterminee' ? 'Indéterminée' : `${offer.duration} mois`})
                      </span>
                    )}
                  </div>
                  <div className={`col-span-2 flex items-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <MapPin className="w-4 h-4 mr-2" />
                    {offer.location}
                  </div>
                  <div className="col-span-4 flex items-center gap-2">
                    <UrgencyBadge level={offer.urgencyLevel} />
                    <JobStatusBadge status={offer.status} />
                    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${darkMode ? 'bg-blue-900 text-blue-300 border border-blue-800' : 'bg-blue-50 text-blue-700 border border-blue-100'}`}>
                      <Users className="w-3.5 h-3.5" />
                      {offer.candidates?.length || 0} candidature{(offer.candidates?.length || 0) > 1 ? 's' : ''}
                    </div>
                    {isAi && offer.sourceUrl && (
                      <a
                        href={offer.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-colors"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Voir l'offre
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination controls */}
      <div className="flex flex-col items-center mt-6 mb-16 gap-4">
        <div className="flex items-center gap-2">
          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Afficher</span>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className={`border ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-200'} rounded-lg p-1 text-sm`}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>offres par page</span>
        </div>

        <div className="flex items-center gap-2">
          <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            {filteredOffers.length > 0 ? `${startIndex + 1}-${Math.min(endIndex, filteredOffers.length)} sur ${filteredOffers.length}` : '0-0 sur 0'}
          </span>

          <div className="flex gap-1">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1 || filteredOffers.length === 0}
              className={`p-2 border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'} rounded-lg disabled:opacity-50`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageToShow;
              if (totalPages <= 5) {
                pageToShow = i + 1;
              } else if (currentPage <= 3) {
                pageToShow = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageToShow = totalPages - 4 + i;
              } else {
                pageToShow = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageToShow}
                  onClick={() => handlePageChange(pageToShow)}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg ${
                    currentPage === pageToShow
                      ? 'bg-gradient-anthea text-white'
                      : `border ${darkMode ? 'border-gray-600 hover:bg-gray-700 text-gray-300' : 'border-gray-200 hover:bg-gray-50'}`
                  }`}
                >
                  {pageToShow}
                </button>
              );
            })}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || filteredOffers.length === 0}
              className={`p-2 border ${darkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 hover:bg-gray-50'} rounded-lg disabled:opacity-50`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {showNewForm && (
        <JobOfferForm
          onSubmit={handleSubmit}
          onClose={() => {
            setShowNewForm(false);
            setEditingOffer(null);
          }}
          offer={editingOffer}
          isEditing={!!editingOffer}
        />
      )}

      {selectedOffer && !showNewForm && (
        <JobOfferDetails
          offer={offers.find(o => o.id === selectedOffer.id) || selectedOffer}
          onClose={() => setSelectedOffer(null)}
          onEdit={() => handleEdit(selectedOffer)}
          onDelete={() => handleDelete(selectedOffer.id)}
        />
      )}

      {showFilters && (
        <JobFilters
          isOpen={showFilters}
          onClose={() => setShowFilters(false)}
          onApplyFilters={() => {
            setShowFilters(false);
          }}
        />
      )}

      {showImport && (
        <JobOffersImport
          onImport={handleImport}
          onClose={() => setShowImport(false)}
        />
      )}
    </div>
  );
}